import type {EffectDefinition,EffectDescriptor} from 'remotion';
import {applyMasks,normToPx,regionRectAt} from '../../../../src/utils/videoMask';
import {readCinematicMasks,type CinematicMasks} from '../../protocol/cinematic-masks';
export function maskNeedsRendering(value:unknown){try{return readCinematicMasks(value)?.regions.some(r=>r.enabled)??false;}catch{return true;}}
type Params={masks:CinematicMasks;time:number};
function readParams(raw:unknown):Params{
  const p=raw as Params;const masks=readCinematicMasks(p?.masks);
  if(!masks||!Number.isFinite(p.time))throw new Error('모자이크 시간 정보를 확인하지 못했습니다.');
  return {masks,time:p.time};
}
/** Same fitted canvas and ordered downsample/nearest-upsample operation as the original encoder. */
export function paintCinematicMaskFrame(source:CanvasImageSource,target:HTMLCanvasElement,reference:HTMLCanvasElement,params:Params,width:number,height:number){
  const {masks,time}=readParams(params);
  reference.width=masks.canvasWidth;reference.height=masks.canvasHeight;
  const ctx=reference.getContext('2d'),out=target.getContext('2d');
  if(!ctx||!out)throw new Error('모자이크 처리 화면을 만들지 못했습니다.');
  ctx.fillStyle='#000000';ctx.fillRect(0,0,reference.width,reference.height);
  const {x,y,w,h}=masks.fit;
  ctx.drawImage(source,x,y,w,h);
  applyMasks(ctx,masks.regions.filter(r=>r.enabled).map(r=>r.mask),time,x,y,w,h,{w:reference.width,h:reference.height},true);
  // Assemble offscreen first: an allocation/draw failure must never publish an unmasked frame.
  const composite=document.createElement('canvas');composite.width=width;composite.height=height;
  const complete=composite.getContext('2d');if(!complete)throw new Error('모자이크 프레임을 완성하지 못했습니다.');
  complete.drawImage(source,0,0,width,height);
  complete.imageSmoothingEnabled=false;
  for(const entry of masks.regions){
    if(!entry.enabled)continue;
    const rect=regionRectAt(entry.mask,time);if(!rect)continue;
    const canvasSpace=entry.mask.space==='canvas';
    const box=normToPx(rect,canvasSpace?0:x,canvasSpace?0:y,canvasSpace?reference.width:w,canvasSpace?reference.height:h);
    if(box.w<1||box.h<1)continue;
    complete.drawImage(reference,box.x,box.y,box.w,box.h,(box.x-x)/w*width,(box.y-y)/h*height,box.w/w*width,box.h/h*height);
  }
  out.clearRect(0,0,width,height);
  out.drawImage(composite,0,0);
}
const definition:EffectDefinition<unknown>={
  type:'waka-cinematic-mosaic',label:'모자이크',documentationLink:null,backend:'2d',schema:{},
  calculateKey:params=>JSON.stringify(params),validateParams:params=>{readParams(params);},
  setup:()=>document.createElement('canvas'),cleanup:state=>{const canvas=state as HTMLCanvasElement;canvas.width=0;canvas.height=0;},
  apply:({source,target,state,params,width,height})=>paintCinematicMaskFrame(source,target,state as HTMLCanvasElement,readParams(params),width,height),
};
export function cinematicMaskEffects(value:unknown,sourceUrl:string|null,sourceTime:number):EffectDescriptor<unknown>[]{
  const masks=readCinematicMasks(value);
  if(!masks||!masks.regions.some(r=>r.enabled))return [];
  if(masks.sourceUrl!==sourceUrl)throw new Error('원본이 바뀌었습니다. 클립의 모자이크 설정에서 현재 원본을 확인해 주세요.');
  const params={masks,time:masks.frozenTime??sourceTime-masks.timeOrigin};readParams(params);
  return [{definition,effectKey:definition.calculateKey(params),params,memoized:false}];
}
